import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";
import { fmtDateTime, useLabels } from "../lib/constants";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const ACTION_STYLES = {
  case_created: "bg-indigo-50 text-indigo-800 border-indigo-200",
  status_changed: "bg-blue-50 text-blue-800 border-blue-200",
  comment_added: "bg-slate-100 text-slate-700 border-slate-200",
  attachment_added: "bg-delta-limeLight text-[#5c7023] border-delta-limeBorder",
  case_assigned: "bg-violet-50 text-violet-700 border-violet-200",
  case_updated: "bg-amber-50 text-amber-800 border-amber-200",
  user_created: "bg-green-50 text-green-700 border-green-200",
  user_updated: "bg-orange-50 text-orange-700 border-orange-200",
  user_login: "bg-slate-50 text-slate-500 border-slate-200",
};

export default function Logs() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const L = useLabels();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (action !== "all") p.set("action", action);
      if (qDebounced) p.set("q", qDebounced);
      const r = await api.get(`/logs?${p.toString()}`);
      setLogs(r.data);
    } catch (e) {
      toast.error(apiError(e, t("logs.loadFail")));
    } finally {
      setLoading(false);
    }
  }, [action, qDebounced]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-5" data-testid="logs-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{t("logs.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("logs.subtitle")}</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-white p-3">
        <div className="relative min-w-52 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="logs-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t("logs.search")} className="ps-9" />
        </div>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger data-testid="logs-action-filter" className="w-52"><SelectValue placeholder={t("logs.title")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("logs.allActions")}</SelectItem>
            {Object.entries(L.action).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-xl border bg-white" data-testid="logs-table">
        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-delta-navy border-t-transparent" />
          </div>
        ) : logs.length === 0 ? (
          <p className="py-16 text-center text-sm text-slate-400" data-testid="logs-empty">{t("logs.empty")}</p>
        ) : (
          <ul className="divide-y">
            {logs.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5" data-testid={`log-entry-${l.id}`}>
                <Badge variant="outline" className={`border text-xs font-medium ${ACTION_STYLES[l.action] || "bg-slate-100 text-slate-600"}`}>
                  {L.action[l.action] || l.action}
                </Badge>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-800">{l.detail}</p>
                  <p className="text-xs text-slate-400">
                    {l.actor_name}
                    {l.case_number && (
                      <>
                        {" · "}
                        <button data-testid={`log-case-link-${l.case_number}`} onClick={() => navigate(`/cases/${l.case_id}`)}
                          className="font-mono font-semibold text-delta-navy hover:underline">
                          {l.case_number}
                        </button>
                      </>
                    )}
                  </p>
                </div>
                <span className="text-xs text-slate-400">{fmtDateTime(l.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
